import { uniq } from 'es-toolkit';
import { API_CONFIG } from '@/config/api';
import { entityService } from '@/services/EntityService';
import { httpClient } from '@/utils/httpClient';
import { isUuid as isValidUuid } from '@equip/utils';
import type { Entity } from '@/types/Entity';
import { isPnpCatalogEntity } from '@/utils/iemFilters';

export type BulkInventoryBinServiceType = 'Serviceable' | 'NonServiceable' | string;
export type BulkInventoryBinConditionCode = 'H' | 'A' | 'B';

export interface BulkInventoryBinRecord {
  id: string;
  serviceType: BulkInventoryBinServiceType;
  conditionCode?: BulkInventoryBinConditionCode | null;
  workflowStatus?: string;
  dateProcessed?: string | null;
  assetId?: string | null;
  assetName: string;
  assetLin?: string | null;
  assetNsn?: string | null;
  assetCatalogId?: string | null;
  assetLocationId?: string | null;
  catalogRecoverable?: string | null;
  catalogSize?: string | null;
  /** Location ID from existing barcode mapping for this catalog (if any) */
  barcodeMappingLocationId?: string | null;
  pendingTypeChangeCatalogId?: string | null;
  pendingTypeChangeQuantity?: number | null;
  pendingTypeChangeTargetName?: string | null;
  pendingTypeChangeTargetLin?: string | null;
  pendingTypeChangeTargetNsn?: string | null;
  /** Current asset status key (e.g. 'available', 'shipped'). Null when asset is missing. */
  assetStatusKey?: string | null;
}

export class BulkInventoryBinService {
  async list(): Promise<BulkInventoryBinRecord[]> {
    return this.listByWorkflowStatuses(['pending_review', 'ready_to_shelf']);
  }

  async listPendingDisposal(): Promise<BulkInventoryBinRecord[]> {
    return this.listByWorkflowStatuses(['pending_disposal']);
  }

  private async listByWorkflowStatuses(
    workflowStatuses: string[],
  ): Promise<BulkInventoryBinRecord[]> {
    const bins = await entityService.list({
      type: 'bulk_inventory_bin',
      filters: {
        'entity_data->>workflow_status': `in.(${workflowStatuses.join(',')})`,
      },
      order: 'entity_data->>service_type.asc,display_name.asc',
      limit: 1000,
    });

    if (!bins.length) {
      return [];
    }

    const assetIds = bins
      .map((bin) => {
        const rawAssetId = bin.entity_data?.asset_id;
        return typeof rawAssetId === 'string' ? rawAssetId : null;
      })
      .filter(isValidUuid);
    const pendingTargetCatalogIds = bins
      .map((bin) => {
        const rawCatalogId = bin.entity_data?.pending_type_change_catalog_id;
        return typeof rawCatalogId === 'string' ? rawCatalogId : null;
      })
      .filter(isValidUuid);

    const uniqueAssetIds = uniq(assetIds);

    const assets = uniqueAssetIds.length
      ? await entityService.list({
          type: 'asset',
          filters: {
            id: `in.(${uniqueAssetIds.join(',')})`,
          },
          order: 'display_name.asc',
        })
      : [];

    const assetMap = new Map<string, Entity>(assets.map((asset) => [asset.id, asset]));

    const catalogIds = assets
      .map((asset) => {
        const rawCatalogId = asset.entity_data?.catalog_id;
        return typeof rawCatalogId === 'string' ? rawCatalogId : null;
      })
      .filter(isValidUuid);

    const uniqueCatalogIds = uniq([...catalogIds, ...pendingTargetCatalogIds]);

    const catalogs = uniqueCatalogIds.length
      ? await entityService.list({
          type: 'catalog',
          limit: Math.max(uniqueCatalogIds.length, 100),
          filters: {
            id: `in.(${uniqueCatalogIds.join(',')})`,
          },
        })
      : [];

    const catalogMap = new Map<string, Entity>(catalogs.map((catalog) => [catalog.id, catalog]));

    // Exclude bins whose associated catalog is PnP — PnP assets should not appear in the bin queue
    const nonPnpBins = bins.filter((bin) => {
      const rawAssetId = bin.entity_data?.asset_id;
      const assetId = typeof rawAssetId === 'string' ? rawAssetId : null;
      const asset = assetId ? assetMap.get(assetId) : undefined;
      const rawCatalogId = asset?.entity_data?.catalog_id;
      const catalogId = typeof rawCatalogId === 'string' ? rawCatalogId : null;
      const catalog = catalogId ? catalogMap.get(catalogId) : undefined;
      return !catalog || !isPnpCatalogEntity(catalog);
    });

    // Barcode lookup is only needed for non-PnP catalog IDs
    const nonPnpCatalogIds = uniqueCatalogIds.filter((id) => {
      const catalog = catalogMap.get(id);
      return !catalog || !isPnpCatalogEntity(catalog);
    });

    // Fetch barcode mappings to get suggested shelf locations for each catalog
    const barcodeMappings = nonPnpCatalogIds.length
      ? await httpClient.get<Array<{ catalog_id: string; location_id: string }>>(
          `/api/v1/barcode_mappings?catalog_id=in.(${nonPnpCatalogIds.join(',')})&select=catalog_id,location_id`,
        )
      : [];

    // Map catalog_id -> location_id (use first mapping if multiple exist)
    const barcodeMappingByCatalog = new Map<string, string>();
    for (const mapping of barcodeMappings) {
      if (!barcodeMappingByCatalog.has(mapping.catalog_id)) {
        barcodeMappingByCatalog.set(mapping.catalog_id, mapping.location_id);
      }
    }

    const records = nonPnpBins.map((bin) => {
      const rawServiceType = bin.entity_data?.service_type;
      const serviceType =
        typeof rawServiceType === 'string' && rawServiceType.trim().length > 0
          ? (rawServiceType.trim() as BulkInventoryBinServiceType)
          : 'Unknown';

      const rawAssetId = bin.entity_data?.asset_id;
      const assetId = typeof rawAssetId === 'string' ? rawAssetId : null;
      const asset = assetId ? assetMap.get(assetId) : undefined;

      const assetData = (asset?.entity_data ?? {}) as Record<string, unknown>;

      const assetName =
        (typeof asset?.display_name === 'string' && asset.display_name.trim().length > 0
          ? asset.display_name.trim()
          : typeof assetData.name === 'string'
            ? assetData.name
            : undefined) ?? 'Unknown Asset';

      const assetLin =
        typeof assetData.lin === 'string' && assetData.lin.trim().length > 0
          ? assetData.lin.trim()
          : null;

      const assetNsn =
        typeof assetData.nsn === 'string' && assetData.nsn.trim().length > 0
          ? assetData.nsn.trim()
          : null;

      const rawCatalogId = assetData.catalog_id;
      const catalogId = typeof rawCatalogId === 'string' ? rawCatalogId : null;
      const catalog = catalogId ? catalogMap.get(catalogId) : undefined;
      const rawPendingTargetCatalogId = bin.entity_data?.pending_type_change_catalog_id;
      const pendingTypeChangeCatalogId =
        typeof rawPendingTargetCatalogId === 'string' && isValidUuid(rawPendingTargetCatalogId)
          ? rawPendingTargetCatalogId
          : null;
      const rawPendingTypeChangeQuantity = bin.entity_data?.pending_type_change_quantity;
      const pendingTypeChangeQuantity =
        typeof rawPendingTypeChangeQuantity === 'number' &&
        Number.isInteger(rawPendingTypeChangeQuantity) &&
        rawPendingTypeChangeQuantity > 0
          ? rawPendingTypeChangeQuantity
          : typeof rawPendingTypeChangeQuantity === 'string'
            ? Number.isInteger(Number.parseInt(rawPendingTypeChangeQuantity, 10)) &&
              Number.parseInt(rawPendingTypeChangeQuantity, 10) > 0
              ? Number.parseInt(rawPendingTypeChangeQuantity, 10)
              : null
            : null;
      const pendingTypeChangeTargetCatalog = pendingTypeChangeCatalogId
        ? catalogMap.get(pendingTypeChangeCatalogId)
        : undefined;

      const rawLocationId = assetData.location_id;
      const assetLocationId = typeof rawLocationId === 'string' ? rawLocationId : null;

      const catalogData = (catalog?.entity_data ?? {}) as Record<string, unknown>;
      const pendingTargetCatalogData = (pendingTypeChangeTargetCatalog?.entity_data ??
        {}) as Record<string, unknown>;

      const rawRecoverable = catalogData.recoverable_nonrecoverable;
      const catalogRecoverable =
        typeof rawRecoverable === 'string' && rawRecoverable.trim().length > 0
          ? rawRecoverable.trim()
          : null;
      const catalogSize =
        typeof catalogData.size === 'string' && catalogData.size.trim().length > 0
          ? catalogData.size.trim()
          : null;
      const pendingTypeChangeTargetName =
        (typeof pendingTypeChangeTargetCatalog?.display_name === 'string' &&
        pendingTypeChangeTargetCatalog.display_name.trim().length > 0
          ? pendingTypeChangeTargetCatalog.display_name.trim()
          : typeof pendingTargetCatalogData.name === 'string' &&
              pendingTargetCatalogData.name.trim().length > 0
            ? pendingTargetCatalogData.name.trim()
            : null) ?? null;
      const pendingTypeChangeTargetLin =
        typeof pendingTargetCatalogData.lin === 'string' &&
        pendingTargetCatalogData.lin.trim().length > 0
          ? pendingTargetCatalogData.lin.trim()
          : null;
      const pendingTypeChangeTargetNsn =
        typeof pendingTargetCatalogData.nsn === 'string' &&
        pendingTargetCatalogData.nsn.trim().length > 0
          ? pendingTargetCatalogData.nsn.trim()
          : null;

      const dateProcessedRaw = bin.entity_data?.date_processed;
      const dateProcessed =
        typeof dateProcessedRaw === 'string' && dateProcessedRaw.trim().length > 0
          ? dateProcessedRaw
          : null;

      const conditionCodeRaw = bin.entity_data?.condition_code;
      const normalizedConditionCode =
        typeof conditionCodeRaw === 'string' ? conditionCodeRaw.trim().toUpperCase() : '';
      const conditionCode =
        normalizedConditionCode === 'H' ||
        normalizedConditionCode === 'A' ||
        normalizedConditionCode === 'B'
          ? (normalizedConditionCode as BulkInventoryBinConditionCode)
          : null;

      const workflowStatus =
        typeof bin.entity_data?.workflow_status === 'string'
          ? bin.entity_data.workflow_status
          : undefined;

      // Get location from barcode mapping for this catalog (if no asset location)
      const barcodeMappingLocationId = catalogId
        ? (barcodeMappingByCatalog.get(catalogId) ?? null)
        : null;

      return {
        id: bin.id,
        serviceType,
        conditionCode,
        workflowStatus,
        dateProcessed,
        assetId,
        assetName,
        assetLin,
        assetNsn,
        assetCatalogId: catalogId,
        assetLocationId,
        catalogRecoverable,
        catalogSize,
        barcodeMappingLocationId,
        pendingTypeChangeCatalogId,
        pendingTypeChangeQuantity,
        pendingTypeChangeTargetName,
        pendingTypeChangeTargetLin,
        pendingTypeChangeTargetNsn,
        assetStatusKey: typeof assetData.asset_status_key === 'string'
          ? assetData.asset_status_key.trim() || null
          : null,
      } satisfies BulkInventoryBinRecord;
    });

    return records.sort((a, b) => {
      const serviceComparison = String(a.serviceType).localeCompare(String(b.serviceType));
      if (serviceComparison !== 0) {
        return serviceComparison;
      }
      return a.assetName.localeCompare(b.assetName);
    });
  }

  async processBins(
    request: ProcessBulkInventoryBinsRequest,
  ): Promise<ProcessBulkInventoryBinsResponse> {
    return httpClient.post(API_CONFIG.ENDPOINTS.BULK_INVENTORY_BIN_COMPLETE, request);
  }

  async completeDisposal(
    request: CompleteBulkDisposeRequest,
  ): Promise<CompleteBulkDisposeResponse> {
    return httpClient.post(API_CONFIG.ENDPOINTS.BULK_DISPOSE_COMPLETE, request);
  }

  async updateServiceType(request: UpdateServiceTypeRequest): Promise<UpdateServiceTypeResponse> {
    return httpClient.post(API_CONFIG.ENDPOINTS.BULK_INVENTORY_BIN_UPDATE_SERVICE_TYPE, request);
  }

  async updateConditionCode(
    request: UpdateConditionCodeRequest,
  ): Promise<UpdateConditionCodeResponse> {
    return httpClient.post(API_CONFIG.ENDPOINTS.BULK_INVENTORY_BIN_UPDATE_CONDITION_CODE, request);
  }

  async updatePendingTypeChange(
    request: UpdatePendingTypeChangeRequest,
  ): Promise<UpdatePendingTypeChangeResponse> {
    return httpClient.post(API_CONFIG.ENDPOINTS.BULK_DISPOSE_PENDING_TYPE_CHANGE, request);
  }

  async adjustType(request: BulkDisposeAdjustTypeRequest): Promise<BulkDisposeAdjustTypeResponse> {
    return httpClient.post(API_CONFIG.ENDPOINTS.BULK_DISPOSE_ADJUST_TYPE, request);
  }

  async returnToInventoryFromDisposal(
    request: DisposeReturnToInventoryRequest,
  ): Promise<DisposeReturnToInventoryResponse> {
    return httpClient.post(API_CONFIG.ENDPOINTS.DISPOSE_RETURN_TO_INVENTORY, request);
  }
}

export const bulkInventoryBinService = new BulkInventoryBinService();

export interface ProcessBulkInventoryBinsRequest {
  returnBinIds: string[];
  disposeBinIds: string[];
  returnBinTargets?: Array<{
    binId: string;
    targetLocationId: string;
  }>;
}

export interface ProcessBulkInventoryBinsResponse {
  processedAt: string;
  returnBinIds: string[];
  disposeBinIds: string[];
  disabledAssetIds: string[];
}

export interface UpdateServiceTypeRequest {
  updates: Array<{
    binId: string;
    serviceType: 'Serviceable' | 'NonServiceable';
  }>;
}

export interface UpdateServiceTypeResponse {
  updatedAt: string;
  updatedBinIds: string[];
}

export interface UpdateConditionCodeRequest {
  binIds: string[];
  conditionCode: BulkInventoryBinConditionCode;
}

export interface UpdateConditionCodeResponse {
  updatedAt: string;
  conditionCode: BulkInventoryBinConditionCode;
  updatedBinIds: string[];
}

export interface UpdatePendingTypeChangeRequest {
  binIds: string[];
  targetCatalogId?: string | null;
  quantity?: number;
}

export interface UpdatePendingTypeChangeResponse {
  updatedAt: string;
  targetCatalogId: string | null;
  quantity: number;
  markedBinIds: string[];
  clearedBinIds: string[];
  updatedBinIds: string[];
}

export interface BulkDisposeAdjustTypeRequest {
  binIds: string[];
  note?: string;
  justification?: string;
}

export interface BulkDisposeAdjustTypeResponse {
  processedAt: string;
  processedBinIds: string[];
  disposedAssetIds: string[];
  createdAssetIds: string[];
  warnings?: string[];
  transactionLogId?: string;
  da4949GeneratedDocumentId?: string;
  da4949DocumentEntityId?: string;
  da4949DocumentNumber?: string | null;
}

export type ManualEntryPayload = {
  lin: string;
  nsn: string;
  nomenclature: string;
  quantity: number;
  conditionCode: string;
  remarks: string;
};

export type CompleteBulkDisposeRequest =
  | {
      binIds: string[];
      assetIds?: never;
      manualEntries?: ManualEntryPayload[];
      shipFrom?: string;
      shipTo?: string;
    }
  | {
      assetIds: string[];
      conditionCodeMap?: Record<string, string>;
      binIds?: never;
      manualEntries?: never;
      shipFrom?: string;
      shipTo?: string;
    }
  | {
      manualEntries: ManualEntryPayload[];
      binIds?: never;
      assetIds?: never;
      shipFrom?: string;
      shipTo?: string;
    };

export interface CompleteBulkDisposeResponse {
  processedAt: string;
  disposedBinIds: string[];
  disposedAssetIds: string[];
  transactionLogId: string;
  dd1348Document?: {
    documentNumber: string;
    documentNumbers: string[];
    documentEntityId: string;
    generatedDocumentId: string;
    pageCount: number;
  };
  dd1348PageCount?: number;
}

export interface DisposeReturnToInventoryRequest {
  binIds: string[];
}

export interface DisposeReturnToInventoryResponse {
  processedAt: string;
  completedBinIds: string[];
  updatedAssetIds: string[];
  skippedAssetIds?: string[];
  warnings?: string[];
}
